import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import api from '../services/api';

// ─── Modos de aprovação combinados ───────────────────────────────────────────
const MODOS_APROVACAO = [
  {
    valor: 'simples',
    rotulo: 'Simples',
    badge: null,
    formula: '|erro| ≤ EMP',
    desc: 'Apenas o erro é comparado ao EMP — incerteza ignorada na decisão',
    detalheInterno: 'Interna: nenhum cálculo de incerteza exigido.',
    detalheExterno: 'Externa: U do certificado não entra na comparação.',
    quando: 'Empresas que registram histórico sem exigência metrológica formal. Adequado para rastreamento simples sem ISO 17025.',
    norma: null,
  },
  {
    valor: 'gum_ilac',
    rotulo: 'ILAC G8',
    badge: 'Recomendado',
    formula: '|erro| + U ≤ EMP',
    desc: 'Incerteza expandida incluída na decisão — critério mais rigoroso e rastreável',
    detalheInterno: 'Interna: U calculado via GUM (u_A das leituras + u_B do padrão).',
    detalheExterno: 'Externa: U informado diretamente do certificado do laboratório.',
    quando: 'Indústria regulada (ANVISA, INMETRO), quem segue ISO/IEC 17025 ou exige rastreabilidade formal da decisão.',
    norma: 'ILAC G8:09/2019 · GUM JCGM 100:2008 · ISO 10012 §7.1.3',
  },
  {
    valor: 'gum_doc',
    rotulo: 'GUM documentado',
    badge: null,
    formula: '|erro| ≤ EMP  (U registrado no laudo)',
    desc: 'U calculado e exibido no certificado, mas a decisão usa apenas o erro',
    detalheInterno: 'Interna: GUM executado normalmente; U aparece no laudo mas não bloqueia aprovação.',
    detalheExterno: 'Externa: U do certificado consta no registro, mas não entra no critério.',
    quando: 'Quando o cliente exige U documentado no laudo mas o critério contratual de aceite é somente por erro.',
    norma: null,
  },
];

// ─── Seções do formulário ─────────────────────────────────────────────────────
const SECOES = [
  { id: 'permissoes',   rotulo: '🔐 Permissões',    desc: 'O que cada perfil pode ver e fazer' },
  { id: 'identidade',   rotulo: '🏢 Identidade',    desc: 'Nome e cabeçalho dos laudos' },
  { id: 'datas',        rotulo: '📅 Datas',          desc: 'Formato e critério de vencimento' },
  { id: 'laudos',       rotulo: '📄 Laudos',         desc: 'Conteúdo dos certificados PDF' },
  { id: 'alertas',      rotulo: '🔔 Alertas',        desc: 'Dias de antecedência para notificar' },
  { id: 'calibracoes',  rotulo: '⚙️ Calibrações',   desc: 'Modo de aprovação e tipo permitido' },
  { id: 'escalas',      rotulo: '📐 Escalas',        desc: 'Grandezas e unidades de medição' },
  { id: 'laboratorios', rotulo: '🔬 Laboratórios',   desc: 'Labs externos e acreditações' },
  { id: 'procedimentos',rotulo: '📋 POPs',           desc: 'Procedimentos operacionais padrão' },
  { id: 'sistema',      rotulo: '🛠️ Sistema',        desc: 'Manutenção e sincronização de dados' },
];

const DIAS_ALERTA_OPCOES = [7, 14, 30, 45, 60, 90];

export default function SettingsPage() {
  const { user } = useAuth();
  const { recarregarTenant } = useTenant();
  const [secaoAtiva, setSecaoAtiva] = useState('usuarios');
  const [form, setForm] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [toast, setToast] = useState(null);
  const [erro, setErro] = useState('');

  const isAdmin = user?.role === 'supervisor';
  const isPlataformAdmin = user?.email === 'brunoruotulo@gmail.com';
  const somenteExterna = form?.planoCalibracao === 'external_only';

  // Modo combinado: lê dois campos → uma opção; grava uma opção → dois campos
  const modoCombinado = !form ? 'gum_ilac'
    : form.modoAprovacao === 'simplificado' ? 'simples'
    : form.criterioAprovacao === 'conservador' ? 'gum_ilac'
    : 'gum_doc';

  function setModoCombinado(modo) {
    if (modo === 'simples')   setForm(p => ({ ...p, modoAprovacao: 'simplificado', criterioAprovacao: 'simples' }));
    if (modo === 'gum_ilac')  setForm(p => ({ ...p, modoAprovacao: 'completo',     criterioAprovacao: 'conservador' }));
    if (modo === 'gum_doc')   setForm(p => ({ ...p, modoAprovacao: 'completo',     criterioAprovacao: 'simples' }));
  }

  useEffect(() => {
    async function buscar() {
      try {
        const { data } = await api.get('/tenant');
        setForm({
          nomeEmpresa: data.cabecalhoLaudo?.nomeEmpresa || data.name || '',
          logoUrl: data.cabecalhoLaudo?.logoUrl || '',
          rodape: data.cabecalhoLaudo?.rodape || '',
          formatoData: data.formatoData || 'DD/MM/AAAA',
          usarDiaExato: data.usarDiaExato !== undefined ? data.usarDiaExato : true,
          exibirPadraoRastreabilidade: data.exibirPadraoRastreabilidade !== undefined
            ? data.exibirPadraoRastreabilidade
            : true,
          diasAlerta: data.diasAlerta || [60, 30, 7],
          modoAprovacao: data.modoAprovacao || 'completo',
          criterioAprovacao: data.criterioAprovacao || 'conservador',
          planoCalibracao: data.planoCalibracao || 'full',
          sessionTimeoutHours: data.sessionTimeoutHours || 4,
        });
      } catch (err) {
        setErro('Não foi possível carregar as configurações.');
      } finally {
        setCarregando(false);
      }
    }
    buscar();
  }, []);

  function exibirToast(mensagem, tipo = 'sucesso') {
    setToast({ mensagem, tipo });
    setTimeout(() => setToast(null), 3500);
  }

  async function handleSalvar() {
    if (!isAdmin) return;
    setSalvando(true);
    setErro('');
    try {
      await api.put('/tenant', {
        cabecalhoLaudo: {
          nomeEmpresa: form.nomeEmpresa,
          logoUrl: form.logoUrl,
          rodape: form.rodape,
        },
        formatoData: form.formatoData,
        usarDiaExato: form.usarDiaExato,
        exibirPadraoRastreabilidade: form.exibirPadraoRastreabilidade,
        diasAlerta: form.diasAlerta,
        modoAprovacao: form.modoAprovacao,
        criterioAprovacao: form.criterioAprovacao,
        planoCalibracao: form.planoCalibracao,
        sessionTimeoutHours: form.sessionTimeoutHours,
      });
      exibirToast('Configurações salvas com sucesso.');
      recarregarTenant();
    } catch (err) {
      const msg = err.response?.data?.mensagem || 'Erro ao salvar. Tente novamente.';
      setErro(msg);
    } finally {
      setSalvando(false);
    }
  }

  function toggleDiaAlerta(dia) {
    setForm((prev) => {
      const atual = prev.diasAlerta || [];
      const novo = atual.includes(dia)
        ? atual.filter((d) => d !== dia)
        : [...atual, dia].sort((a, b) => b - a);
      return { ...prev, diasAlerta: novo };
    });
  }

  // ─── Acesso negado ───────────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-500 p-8">
        <span className="text-4xl mb-3">🔒</span>
        <p className="text-sm">Apenas administradores podem acessar as configurações.</p>
      </div>
    );
  }

  if (carregando || !form) {
    return (
      <div className="p-8 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-[var(--surface-3)] rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 min-h-full bg-[var(--surface-1)]">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-xl text-sm font-medium ${
          toast.tipo === 'sucesso'
            ? 'bg-emerald-800 text-emerald-100 border border-emerald-700'
            : 'bg-red-800 text-red-100 border border-red-700'
        }`}>
          {toast.mensagem}
        </div>
      )}

      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Configurações</h1>
          <p className="text-slate-500 text-sm mt-0.5">Personalize o CalibraAI para sua empresa</p>
        </div>
        <button
          onClick={handleSalvar}
          disabled={salvando}
          className="px-5 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {salvando ? 'Salvando...' : '💾 Salvar alterações'}
        </button>
      </div>

      {erro && (
        <div className="mb-4 bg-red-900/40 border border-red-700 text-red-300 rounded-lg px-4 py-3 text-sm">
          {erro}
        </div>
      )}

      <div className="flex gap-6">

        {/* ── Menu lateral de seções ────────────────────────────────────────── */}
        <div className="w-52 flex-shrink-0 space-y-1">
          {SECOES.map((s) => (
            <button
              key={s.id}
              onClick={() => setSecaoAtiva(s.id)}
              className={`w-full text-left px-3 py-3 rounded-lg transition-colors ${
                secaoAtiva === s.id
                  ? 'bg-blue-700/30 border border-blue-700/50 text-blue-300'
                  : 'text-slate-400 hover:bg-[var(--surface-3)] hover:text-slate-200'
              }`}
            >
              <div className="text-sm font-medium">{s.rotulo}</div>
              <div className="text-xs text-slate-600 mt-0.5">{s.desc}</div>
            </button>
          ))}
        </div>

        {/* ── Conteúdo da seção ─────────────────────────────────────────────── */}
        <div className="flex-1 bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl p-6 space-y-6">

          {/* ── Seção: Identidade ──────────────────────────────────────────── */}
          {secaoAtiva === 'identidade' && (
            <>
              <div>
                <h2 className="text-base font-semibold text-slate-100 mb-1">Identidade da Empresa</h2>
                <p className="text-xs text-slate-500">Estas informações aparecem no cabeçalho dos laudos gerados.</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1.5">
                    Nome da Empresa (cabeçalho do laudo)
                  </label>
                  <input
                    type="text"
                    value={form.nomeEmpresa}
                    onChange={(e) => setForm((p) => ({ ...p, nomeEmpresa: e.target.value }))}
                    placeholder="Ex: Indústria Metalúrgica Paulista Ltda"
                    className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1.5">
                    URL da Logo
                    <span className="ml-2 text-xs text-slate-600">(upload direto em breve)</span>
                  </label>
                  <input
                    type="url"
                    value={form.logoUrl}
                    onChange={(e) => setForm((p) => ({ ...p, logoUrl: e.target.value }))}
                    placeholder="https://exemplo.com/logo.png"
                    className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500 text-sm"
                  />
                  {form.logoUrl && (
                    <div className="mt-2 p-3 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg inline-block">
                      <img
                        src={form.logoUrl}
                        alt="Preview da logo"
                        className="h-10 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1.5">Rodapé do Laudo</label>
                  <textarea
                    value={form.rodape}
                    onChange={(e) => setForm((p) => ({ ...p, rodape: e.target.value }))}
                    rows={3}
                    placeholder="Ex: Este certificado foi emitido conforme os requisitos da ISO/IEC 17025."
                    className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500 text-sm resize-none"
                  />
                </div>
              </div>
            </>
          )}

          {/* ── Seção: Datas ───────────────────────────────────────────────── */}
          {secaoAtiva === 'datas' && (
            <>
              <div>
                <h2 className="text-base font-semibold text-slate-100 mb-1">Preferências de Data</h2>
                <p className="text-xs text-slate-500">Define como as datas de próxima calibração são exibidas. MM/AAAA usa o último dia do mês para calcular vencimento.</p>
              </div>
              <div className="space-y-6">
                {/* Formato de data */}
                <div>
                  <label className="block text-sm text-slate-300 mb-3">Formato de exibição de datas</label>
                  <div className="flex gap-3">
                    {['DD/MM/AAAA', 'MM/AAAA'].map((fmt) => (
                      <button
                        key={fmt}
                        type="button"
                        onClick={() => setForm((p) => ({ ...p, formatoData: fmt }))}
                        className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all ${
                          form.formatoData === fmt
                            ? 'bg-blue-700 border-blue-500 text-white'
                            : 'bg-[var(--surface-1)] border-[var(--surface-4)] text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        {fmt}
                        <span className="block text-xs font-normal mt-0.5 opacity-75">
                          {fmt === 'DD/MM/AAAA' ? 'Ex: 15/03/2025' : 'Ex: 03/2025'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Timeout de sessão */}
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Tempo de sessão (horas)</label>
                  <p className="text-xs text-slate-500 mb-3">Após esse período sem nova atividade o usuário precisará fazer login novamente.</p>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max="720"
                      value={form.sessionTimeoutHours}
                      onChange={e => setForm(p => ({ ...p, sessionTimeoutHours: parseInt(e.target.value) || 4 }))}
                      className="w-24 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-blue-500 text-center"
                    />
                    <span className="text-sm text-slate-400">horas</span>
                    <span className="text-xs text-slate-500">(padrão: 4h · máx: 720h = 30 dias)</span>
                  </div>
                </div>

              </div>
            </>
          )}

          {/* ── Seção: Laudos ──────────────────────────────────────────────── */}
          {secaoAtiva === 'laudos' && (
            <>
              <div>
                <h2 className="text-base font-semibold text-slate-100 mb-1">Conteúdo dos Laudos</h2>
                <p className="text-xs text-slate-500">Controla quais seções aparecem nos certificados PDF gerados.</p>
              </div>
              <div className="space-y-4">
                <ToggleOpcao
                  ativo={form.exibirPadraoRastreabilidade}
                  onChange={(v) => setForm((p) => ({ ...p, exibirPadraoRastreabilidade: v }))}
                  rotulo="Exibir padrão de rastreabilidade"
                  desc="Inclui a seção do padrão utilizado (descrição, nº certificado, validade) no PDF do laudo."
                />
              </div>
            </>
          )}

          {/* ── Seção: Alertas ─────────────────────────────────────────────── */}
          {secaoAtiva === 'alertas' && (
            <>
              <div>
                <h2 className="text-base font-semibold text-slate-100 mb-1">Alertas de Vencimento</h2>
                <p className="text-xs text-slate-500">Selecione com quantos dias de antecedência o sistema deve alertar sobre vencimentos.</p>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-3">
                  Dias de antecedência selecionados:{' '}
                  <span className="text-blue-400 font-medium">
                    {(form.diasAlerta || []).sort((a, b) => b - a).join(', ')} dias
                  </span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {DIAS_ALERTA_OPCOES.map((dia) => {
                    const ativo = (form.diasAlerta || []).includes(dia);
                    return (
                      <button
                        key={dia}
                        type="button"
                        onClick={() => toggleDiaAlerta(dia)}
                        className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                          ativo
                            ? 'bg-blue-700 border-blue-500 text-white'
                            : 'bg-[var(--surface-1)] border-[var(--surface-4)] text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        {dia} dias
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-600 mt-3">
                  Mínimo de 1 opção selecionada. Os alertas aparecem no dashboard e nas notificações.
                </p>
              </div>
            </>
          )}

          {/* ── Seção: Permissões ─────────────────────────────────────────── */}
          {secaoAtiva === 'permissoes' && <SecaoPermissoes />}

          {/* ── Seção: Escalas ────────────────────────────────────────────── */}
          {secaoAtiva === 'escalas' && <SecaoEscalas />}

          {/* ── Seção: Laboratórios ───────────────────────────────────────── */}
          {secaoAtiva === 'laboratorios' && <SecaoLaboratorios />}

          {/* ── Seção: Procedimentos ──────────────────────────────────────── */}
          {secaoAtiva === 'procedimentos' && <SecaoProcedimentos />}

          {/* ── Seção: Sistema ──────────────────────────────────────────────── */}
          {secaoAtiva === 'sistema' && <SecaoSistema />}

          {/* ── Seção: Calibrações ──────────────────────────────────────────── */}
          {secaoAtiva === 'calibracoes' && (
            <>
              <div>
                <h2 className="text-base font-semibold text-slate-100 mb-1">Configurações de Calibração</h2>
                <p className="text-xs text-slate-500">Define o fluxo de aprovação e os tipos de calibração disponíveis.</p>
              </div>
              <div className="space-y-6">

                {/* ── Modo de aprovação (seletor único combinado) ─────────── */}
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Modo de aprovação</label>
                  <p className="text-xs text-slate-500 mb-3">
                    Pode ser sobrescrito por Classe de Instrumento para tipos específicos.
                  </p>
                  <div className="flex flex-col gap-2">
                    {MODOS_APROVACAO.map(op => {
                      const ativo = modoCombinado === op.valor;
                      return (
                        <button
                          key={op.valor}
                          type="button"
                          onClick={() => setModoCombinado(op.valor)}
                          className={`w-full py-3 px-4 rounded-lg border text-left transition-all ${
                            ativo
                              ? 'bg-blue-700 border-blue-500 text-white'
                              : 'bg-[var(--surface-1)] border-[var(--surface-4)] text-slate-400 hover:border-slate-500'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">{op.rotulo}</span>
                            {op.badge && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                ativo ? 'bg-blue-500 text-white' : 'bg-emerald-900/60 text-emerald-400 border border-emerald-700/50'
                              }`}>
                                {op.badge}
                              </span>
                            )}
                            <span className={`ml-auto font-mono text-xs ${ativo ? 'text-blue-200' : 'text-slate-600'}`}>
                              {op.formula}
                            </span>
                          </div>
                          <span className={`block text-xs font-normal mt-0.5 ${ativo ? 'text-blue-200' : 'text-slate-600'}`}>
                            {op.desc}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* ── Painel de ajuda ─────────────────────────────────────── */}
                <PainelAjudaModo modoAtivo={modoCombinado} />

                {/* ── Tipos de calibração permitidos ──────────────────────── */}
                <div>
                  <label className="block text-sm text-slate-300 mb-3">Tipos de calibração permitidos</label>
                  {somenteExterna && !isPlataformAdmin ? (
                    <div className="rounded-lg border border-amber-800/50 bg-amber-900/20 p-4 flex items-start gap-3">
                      <span className="text-amber-400 text-lg mt-0.5">🔒</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-300">Plano atual: Somente Externa</p>
                        <p className="text-xs text-amber-500 mt-0.5">Sua empresa está configurada para calibrações externas apenas. Para habilitar calibrações internas, solicite um upgrade.</p>
                        <a
                          href="mailto:brunoruotulo@gmail.com?subject=Solicitar upgrade de plano — CalibraAi"
                          className="inline-block mt-3 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold transition-colors"
                        >
                          Solicitar upgrade
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-3">
                      {[
                        { valor: 'full', rotulo: 'Interna + Externa', desc: 'Empresa realiza calibrações internas com padrões próprios' },
                        { valor: 'external_only', rotulo: 'Somente Externa', desc: 'Calibrações sempre realizadas por laboratório terceiro' },
                      ].map(op => (
                        <button
                          key={op.valor}
                          type="button"
                          onClick={() => setForm(p => ({ ...p, planoCalibracao: op.valor }))}
                          className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all text-left ${
                            form.planoCalibracao === op.valor
                              ? 'bg-blue-700 border-blue-500 text-white'
                              : 'bg-[var(--surface-1)] border-[var(--surface-4)] text-slate-400 hover:border-slate-500'
                          }`}
                        >
                          {op.rotulo}
                          <span className="block text-xs font-normal mt-0.5 opacity-75">{op.desc}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Painel de ajuda: modo de aprovação ──────────────────────────────────────
function PainelAjudaModo({ modoAtivo }) {
  const [aberto, setAberto] = useState(false);
  const modo = MODOS_APROVACAO.find(m => m.valor === modoAtivo);

  return (
    <div className="rounded-lg border border-[var(--surface-4)] overflow-hidden">
      {/* Cabeçalho clicável */}
      <button
        type="button"
        onClick={() => setAberto(a => !a)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[var(--surface-1)] text-left hover:bg-[var(--surface-3)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">📖</span>
          <span className="text-xs font-medium text-slate-300">Entender os modos de aprovação</span>
          <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/40 text-blue-400 border border-blue-800/40 rounded">
            {modo?.rotulo}
          </span>
        </div>
        <span className="text-slate-600 text-xs">{aberto ? '▲' : '▼'}</span>
      </button>

      {/* Conteúdo expandido */}
      {aberto && (
        <div className="px-4 py-4 bg-[var(--surface-1)] border-t border-[var(--surface-4)] space-y-5">

          {/* Tabela comparativa */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Comparativo</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-separate border-spacing-0">
                <thead>
                  <tr className="text-slate-500">
                    <th className="text-left py-1.5 pr-4 font-medium w-32">Modo</th>
                    <th className="text-left py-1.5 pr-4 font-medium">Fórmula de decisão</th>
                    <th className="text-left py-1.5 pr-4 font-medium">GUM na interna?</th>
                    <th className="text-left py-1.5 font-medium">Norma de referência</th>
                  </tr>
                </thead>
                <tbody>
                  {MODOS_APROVACAO.map(m => (
                    <tr
                      key={m.valor}
                      className={`border-t border-[var(--surface-4)] ${m.valor === modoAtivo ? 'bg-blue-950/30' : ''}`}
                    >
                      <td className="py-2 pr-4 font-semibold text-slate-300">
                        {m.rotulo}
                        {m.valor === modoAtivo && <span className="ml-1.5 text-[9px] text-blue-400">← atual</span>}
                      </td>
                      <td className="py-2 pr-4 font-mono text-emerald-400">{m.formula}</td>
                      <td className="py-2 pr-4 text-slate-400">
                        {m.valor === 'simples' ? 'Não exigido' : 'Sim, obrigatório'}
                      </td>
                      <td className="py-2 text-slate-500">{m.norma || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Glossário */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Glossário</p>
            <div className="space-y-1.5 text-xs text-slate-500">
              <p><span className="font-mono text-slate-300">EMP</span> — Erro Máximo Permitido. Limite de tolerância definido por classe ou grandeza do instrumento.</p>
              <p><span className="font-mono text-slate-300">erro</span> — Diferença entre o valor lido e o valor aplicado (referência): <span className="font-mono text-blue-400">lido − aplicado</span>.</p>
              <p><span className="font-mono text-slate-300">U</span> — Incerteza expandida (k=2, ~95% de confiança). Calculada via GUM: <span className="font-mono text-blue-400">U = 2·√(u_A² + u_B²)</span>.</p>
              <p><span className="font-mono text-slate-300">u_A</span> — Incerteza tipo A (estatística): <span className="font-mono text-blue-400">u_A = desvio padrão / √n</span> — reduz com mais leituras.</p>
              <p><span className="font-mono text-slate-300">u_B</span> — Incerteza tipo B (do padrão): <span className="font-mono text-blue-400">u_B = U_padrão / k_padrão</span> — vem do certificado do padrão usado.</p>
            </div>
          </div>

          {/* Calibração interna vs externa */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Como U é obtido em cada contexto</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-separate border-spacing-0">
                <thead>
                  <tr className="text-slate-500">
                    <th className="text-left py-1.5 pr-4 font-medium w-36">Modo</th>
                    <th className="text-left py-1.5 pr-4 font-medium">Calibração interna</th>
                    <th className="text-left py-1.5 font-medium">Calibração externa (lab terceiro)</th>
                  </tr>
                </thead>
                <tbody>
                  {MODOS_APROVACAO.map(m => (
                    <tr key={m.valor} className={`border-t border-[var(--surface-4)] ${m.valor === modoAtivo ? 'bg-blue-950/30' : ''}`}>
                      <td className="py-2 pr-4 font-semibold text-slate-300 align-top">{m.rotulo}</td>
                      <td className="py-2 pr-4 text-slate-500 align-top">{m.detalheInterno}</td>
                      <td className="py-2 text-slate-500 align-top">{m.detalheExterno}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-slate-600 mt-2">
              O cálculo GUM (u_A + u_B → U) só ocorre em calibrações internas. Para externas, U vem do certificado emitido pelo laboratório.
            </p>
          </div>

          {/* Quando usar cada um */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Quando usar cada modo</p>
            <div className="space-y-2">
              {MODOS_APROVACAO.map(m => (
                <div key={m.valor} className={`rounded p-3 border ${
                  m.valor === modoAtivo
                    ? 'border-blue-700/50 bg-blue-950/20'
                    : 'border-[var(--surface-4)] bg-transparent'
                }`}>
                  <p className="text-xs font-semibold text-slate-300 mb-0.5">{m.rotulo}</p>
                  <p className="text-xs text-slate-500">{m.quando}</p>
                  {m.norma && (
                    <p className="text-[10px] text-blue-500 mt-1">Normas: {m.norma}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Nota sobre classes */}
          <div className="flex items-start gap-2 p-3 bg-amber-950/20 border border-amber-800/30 rounded-lg">
            <span className="text-amber-400 text-sm flex-shrink-0">ℹ</span>
            <p className="text-xs text-amber-400/80">
              Este modo é o padrão da empresa. Cada <strong>Classe de Instrumento</strong> pode sobrescrever
              para Simples ou Conservador independentemente — útil quando balanças e termômetros
              têm requisitos diferentes de aprovação.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Seção: Escalas ──────────────────────────────────────────────────────────
function SecaoEscalas() {
  const [escalas, setEscalas] = useState([]);
  const [editando, setEditando] = useState(null); // null | 'novo' | { id, grandeza, unidades }
  const [form, setForm] = useState({ grandeza: '', unidades: '' });
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { carregar(); }, []);
  const carregar = async () => {
    try { const { data } = await api.get('/escalas'); setEscalas(data); } catch {}
  };

  const abrirNovo = () => { setForm({ grandeza: '', unidades: '' }); setErro(''); setEditando('novo'); };
  const abrirEditar = (e) => { setForm({ grandeza: e.grandeza, unidades: e.unidades.join(', ') }); setErro(''); setEditando(e); };
  const fechar = () => setEditando(null);

  const salvar = async () => {
    setSalvando(true); setErro('');
    try {
      const payload = { grandeza: form.grandeza.trim(), unidades: form.unidades.split(',').map(u => u.trim()).filter(Boolean) };
      if (editando === 'novo') await api.post('/escalas', payload);
      else await api.put(`/escalas/${editando._id}`, payload);
      await carregar(); fechar();
    } catch (err) { setErro(err.response?.data?.mensagem || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const [confirmando, setConfirmando] = useState(null);

  const excluir = async (id) => {
    try { await api.delete(`/escalas/${id}`); await carregar(); setConfirmando(null); } catch (err) { setErro(err.response?.data?.mensagem || 'Erro ao excluir'); setConfirmando(null); }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-100 mb-1">Escalas de Medição</h2>
          <p className="text-xs text-slate-500">Grandezas e suas unidades disponíveis para seleção nos instrumentos.</p>
        </div>
        <button onClick={abrirNovo} className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 text-white text-xs font-medium rounded-lg">+ Nova Escala</button>
      </div>

      {editando && (
        <div className="mb-4 p-4 bg-[var(--surface-1)] border border-blue-700/40 rounded-lg space-y-3">
          <h3 className="text-sm font-medium text-slate-200">{editando === 'novo' ? 'Nova Escala' : 'Editar Escala'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Grandeza *</label>
              <input value={form.grandeza} onChange={e => setForm(p => ({ ...p, grandeza: e.target.value }))}
                placeholder="Ex: Temperatura" className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Unidades (separadas por vírgula)</label>
              <input value={form.unidades} onChange={e => setForm(p => ({ ...p, unidades: e.target.value }))}
                placeholder="Ex: °C, K, °F" className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500" />
            </div>
          </div>
          {erro && <p className="text-red-400 text-xs">{erro}</p>}
          <div className="flex gap-2">
            <button onClick={salvar} disabled={salvando} className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white text-xs rounded-lg disabled:opacity-50">{salvando ? 'Salvando...' : 'Salvar'}</button>
            <button onClick={fechar} className="px-3 py-1.5 text-slate-400 hover:text-slate-200 text-xs">Cancelar</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {escalas.length === 0 && <p className="text-xs text-slate-600 py-4 text-center">Nenhuma escala cadastrada.</p>}
        {escalas.map(e => (
          <div key={e._id} className="flex items-center justify-between px-4 py-3 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg">
            <div>
              <span className="text-sm font-medium text-slate-200">{e.grandeza}</span>
              <span className="ml-3 text-xs text-slate-500">{e.unidades.join(' · ') || '—'}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => abrirEditar(e)} className="text-xs text-blue-400 hover:text-blue-300">Editar</button>
              {confirmando === e._id ? (
                <>
                  <span className="text-xs text-slate-400">Confirmar?</span>
                  <button onClick={() => excluir(e._id)} className="text-xs text-red-400 hover:text-red-300 font-medium">Sim</button>
                  <button onClick={() => setConfirmando(null)} className="text-xs text-slate-500 hover:text-slate-300">Não</button>
                </>
              ) : (
                <button onClick={() => setConfirmando(e._id)} className="text-xs text-red-400 hover:text-red-300">Excluir</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Seção: Laboratórios ──────────────────────────────────────────────────────
function SecaoLaboratorios() {
  const [labs, setLabs] = useState([]);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nome: '', acreditacao: '', cnpj: '', contato: '', email: '', telefone: '' });
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { carregar(); }, []);
  const carregar = async () => {
    try { const { data } = await api.get('/laboratorios'); setLabs(data); } catch {}
  };

  const abrirNovo = () => { setForm({ nome: '', acreditacao: '', cnpj: '', contato: '', email: '', telefone: '' }); setErro(''); setEditando('novo'); };
  const abrirEditar = (l) => { setForm({ nome: l.nome, acreditacao: l.acreditacao || '', cnpj: l.cnpj || '', contato: l.contato || '', email: l.email || '', telefone: l.telefone || '' }); setErro(''); setEditando(l); };
  const fechar = () => setEditando(null);

  const salvar = async () => {
    setSalvando(true); setErro('');
    try {
      if (editando === 'novo') await api.post('/laboratorios', form);
      else await api.put(`/laboratorios/${editando._id}`, form);
      await carregar(); fechar();
    } catch (err) { setErro(err.response?.data?.mensagem || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const [confirmando, setConfirmando] = useState(null);

  const excluir = async (id) => {
    try { await api.delete(`/laboratorios/${id}`); await carregar(); setConfirmando(null); } catch (err) { setErro(err.response?.data?.mensagem || 'Erro ao excluir'); setConfirmando(null); }
  };

  const campo = (label, key, placeholder, type = 'text') => (
    <div>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
        placeholder={placeholder} className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500" />
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-100 mb-1">Laboratórios Externos</h2>
          <p className="text-xs text-slate-500">Fornecedores de calibração utilizados nas calibrações externas.</p>
        </div>
        <button onClick={abrirNovo} className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 text-white text-xs font-medium rounded-lg">+ Novo Lab</button>
      </div>

      {editando && (
        <div className="mb-4 p-4 bg-[var(--surface-1)] border border-blue-700/40 rounded-lg space-y-3">
          <h3 className="text-sm font-medium text-slate-200">{editando === 'novo' ? 'Novo Laboratório' : 'Editar Laboratório'}</h3>
          <div className="grid grid-cols-2 gap-3">
            {campo('Nome *', 'nome', 'Ex: Lab Metrologia SP Ltda')}
            <div>
              <label className="text-xs text-slate-400 block mb-1">Acreditação</label>
              <select value={form.acreditacao} onChange={e => setForm(p => ({ ...p, acreditacao: e.target.value }))}
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500">
                <option value="">Selecione...</option>
                <option value="RBC">RBC (Rede Brasileira de Calibração)</option>
                <option value="nao_RBC">Não-RBC</option>
                <option value="interno">Laboratório Interno</option>
              </select>
            </div>
            {campo('CNPJ', 'cnpj', '00.000.000/0001-00')}
            {campo('Contato', 'contato', 'Nome do responsável')}
            {campo('E-mail', 'email', 'lab@exemplo.com', 'email')}
            {campo('Telefone', 'telefone', '(11) 99999-9999')}
          </div>
          {erro && <p className="text-red-400 text-xs">{erro}</p>}
          <div className="flex gap-2">
            <button onClick={salvar} disabled={salvando} className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white text-xs rounded-lg disabled:opacity-50">{salvando ? 'Salvando...' : 'Salvar'}</button>
            <button onClick={fechar} className="px-3 py-1.5 text-slate-400 hover:text-slate-200 text-xs">Cancelar</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {labs.length === 0 && <p className="text-xs text-slate-600 py-4 text-center">Nenhum laboratório cadastrado.</p>}
        {labs.map(l => (
          <div key={l._id} className="flex items-center justify-between px-4 py-3 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg">
            <div>
              <span className="text-sm font-medium text-slate-200">{l.nome}</span>
              {l.acreditacao && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-blue-950/60 text-blue-300 border border-blue-800/40 rounded">{l.acreditacao}</span>}
              {l.cnpj && <span className="ml-2 text-xs text-slate-500">{l.cnpj}</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => abrirEditar(l)} className="text-xs text-blue-400 hover:text-blue-300">Editar</button>
              {confirmando === l._id ? (
                <>
                  <span className="text-xs text-slate-400">Confirmar?</span>
                  <button onClick={() => excluir(l._id)} className="text-xs text-red-400 hover:text-red-300 font-medium">Sim</button>
                  <button onClick={() => setConfirmando(null)} className="text-xs text-slate-500 hover:text-slate-300">Não</button>
                </>
              ) : (
                <button onClick={() => setConfirmando(l._id)} className="text-xs text-red-400 hover:text-red-300">Excluir</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Seção: Procedimentos ─────────────────────────────────────────────────────
function SecaoProcedimentos() {
  const [procs, setProcs] = useState([]);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ numero: '', descricao: '' });
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [confirmando, setConfirmando] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [pdfAberto, setPdfAberto] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  useEffect(() => { carregar(); }, []);
  const carregar = async () => {
    try { const { data } = await api.get('/procedimentos'); setProcs(data); } catch {}
  };

  const abrirNovo = () => { setForm({ numero: '', descricao: '' }); setErro(''); setPendingFile(null); setEditando('novo'); };
  const abrirEditar = (p) => { setForm({ numero: p.numero, descricao: p.descricao }); setErro(''); setPendingFile(null); setEditando(p); };
  const fechar = () => { setEditando(null); setPendingFile(null); };

  const salvar = async () => {
    setSalvando(true); setErro('');
    try {
      if (editando === 'novo') {
        const { data: novoProc } = await api.post('/procedimentos', form);
        if (pendingFile) await handleUpload(novoProc._id, pendingFile);
      } else {
        await api.put(`/procedimentos/${editando._id}`, form);
      }
      await carregar(); fechar();
    } catch (err) { setErro(err.response?.data?.mensagem || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const excluir = async (id) => {
    try { await api.delete(`/procedimentos/${id}`); await carregar(); setConfirmando(null); }
    catch (err) { setErro(err.response?.data?.mensagem || 'Erro ao excluir'); setConfirmando(null); }
  };

  const handleUpload = async (id, file) => {
    if (!file) return;
    setUploading(id);
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      const { data } = await api.post(`/procedimentos/${id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProcs(prev => prev.map(p => p._id === data._id ? { ...p, arquivoUrl: data.arquivoUrl } : p));
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao fazer upload.');
    } finally {
      setUploading(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-100 mb-1">Procedimentos (POPs)</h2>
          <p className="text-xs text-slate-500">Procedimentos operacionais padrão referenciados nas calibrações.</p>
        </div>
        <button onClick={abrirNovo} className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 text-white text-xs font-medium rounded-lg">+ Novo POP</button>
      </div>

      {editando && (
        <div className="mb-4 p-4 bg-[var(--surface-1)] border border-blue-700/40 rounded-lg space-y-3">
          <h3 className="text-sm font-medium text-slate-200">{editando === 'novo' ? 'Novo Procedimento' : 'Editar Procedimento'}</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Número *</label>
              <input value={form.numero} onChange={e => setForm(p => ({ ...p, numero: e.target.value }))}
                placeholder="Ex: POP-CAL-001" className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-400 block mb-1">Descrição *</label>
              <input value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))}
                placeholder="Ex: Procedimento de calibração de termômetros" className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500" />
            </div>
          </div>
          {editando === 'novo' && (
            <div>
              <label className="text-xs text-slate-400 block mb-1">PDF do procedimento (opcional)</label>
              <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-1.5 bg-[var(--surface-3)] border border-[var(--surface-4)] rounded text-xs text-slate-300 hover:border-blue-500 transition-colors">
                {pendingFile ? `📄 ${pendingFile.name}` : '↑ Selecionar PDF'}
                <input type="file" accept=".pdf" className="hidden"
                  onChange={e => setPendingFile(e.target.files[0] || null)} />
              </label>
              {pendingFile && (
                <button onClick={() => setPendingFile(null)} className="ml-2 text-xs text-red-400 hover:text-red-300">✕ Remover</button>
              )}
            </div>
          )}
          {erro && <p className="text-red-400 text-xs">{erro}</p>}
          <div className="flex gap-2">
            <button onClick={salvar} disabled={salvando} className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white text-xs rounded-lg disabled:opacity-50">{salvando ? 'Salvando...' : 'Salvar'}</button>
            <button onClick={fechar} className="px-3 py-1.5 text-slate-400 hover:text-slate-200 text-xs">Cancelar</button>
          </div>
        </div>
      )}

      {erro && !editando && <p className="text-red-400 text-xs mb-3">{erro}</p>}

      <div className="space-y-2">
        {procs.length === 0 && <p className="text-xs text-slate-600 py-4 text-center">Nenhum procedimento cadastrado.</p>}
        {procs.map(p => (
          <div key={p._id} className="px-4 py-3 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-mono font-semibold text-indigo-400 flex-shrink-0">{p.numero}</span>
                <span className="text-sm text-slate-200 truncate">{p.descricao}</span>
                {p.arquivoUrl && (
                  <button
                    onClick={() => setPdfAberto(pdfAberto === p._id ? null : p._id)}
                    className="text-[10px] px-1.5 py-0.5 bg-emerald-900/40 text-emerald-400 border border-emerald-700/40 rounded hover:bg-emerald-800/40 flex-shrink-0"
                  >
                    📄 PDF
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                <label className={`text-xs cursor-pointer ${uploading === p._id ? 'text-slate-500' : 'text-slate-400 hover:text-slate-200'}`}>
                  {uploading === p._id ? 'Enviando...' : p.arquivoUrl ? '↑ Trocar PDF' : '↑ Upload PDF'}
                  <input type="file" accept=".pdf" className="hidden" disabled={uploading === p._id}
                    onChange={e => { if (e.target.files[0]) handleUpload(p._id, e.target.files[0]); e.target.value = ''; }} />
                </label>
                <button onClick={() => abrirEditar(p)} className="text-xs text-blue-400 hover:text-blue-300">Editar</button>
                {confirmando === p._id ? (
                  <>
                    <span className="text-xs text-slate-400">Confirmar?</span>
                    <button onClick={() => excluir(p._id)} className="text-xs text-red-400 hover:text-red-300 font-medium">Sim</button>
                    <button onClick={() => setConfirmando(null)} className="text-xs text-slate-500 hover:text-slate-300">Não</button>
                  </>
                ) : (
                  <button onClick={() => setConfirmando(p._id)} className="text-xs text-red-400 hover:text-red-300">Excluir</button>
                )}
              </div>
            </div>
            {pdfAberto === p._id && p.arquivoUrl && (
              <div className="mt-3 border border-[var(--surface-4)] rounded overflow-hidden" style={{ height: 480 }}>
                <embed src={p.arquivoUrl} type="application/pdf" className="w-full h-full" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Seção: Sistema ───────────────────────────────────────────────────────────
function SecaoSistema() {
  const [sincronizando, setSincronizando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');

  const sincronizar = async () => {
    setSincronizando(true);
    setResultado(null);
    setErro('');
    try {
      const { data } = await api.post('/instruments/sync-statuses');
      setResultado(data.mensagem);
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao sincronizar.');
    } finally {
      setSincronizando(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-100 mb-1">Manutenção do Sistema</h2>
        <p className="text-xs text-slate-500">Operações de sincronização e correção de dados.</p>
      </div>

      <div className="p-4 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-slate-200">Sincronizar Status dos Instrumentos</p>
            <p className="text-xs text-slate-500 mt-1">
              Recalcula o status de calibração de todos os instrumentos com base nas calibrações
              realmente existentes no banco. Use se algum instrumento exibir status incorreto
              após deleções ou importações diretas no banco.
            </p>
          </div>
          <button
            onClick={sincronizar}
            disabled={sincronizando}
            className="flex-shrink-0 px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {sincronizando ? 'Sincronizando...' : '🔄 Sincronizar'}
          </button>
        </div>
        {resultado && (
          <p className="mt-3 text-xs text-emerald-400 bg-emerald-950/30 border border-emerald-800/40 rounded px-3 py-2">
            ✓ {resultado}
          </p>
        )}
        {erro && (
          <p className="mt-3 text-xs text-red-400 bg-red-950/30 border border-red-800/40 rounded px-3 py-2">
            {erro}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Seção: Permissões ───────────────────────────────────────────────────────
const MODULOS_LABEL = {
  dashboard:    'Dashboard',
  ativos:       'Ativos',
  instrumentos: 'Instrumentos',
  malhas:       'Malhas',
  calibracoes:  'Calibração',
  padroes:      'Padrões',
  verificacoes: 'Verificações',
  orcamentos:   'Orçamentos',
  calendario:   'Calendário',
  relatorios:   'Relatórios',
  classes:      'Classes',
};

const MODULOS = Object.keys(MODULOS_LABEL);
const ROLES_CONFIG = [
  { key: 'operador', label: 'Operador', cor: 'text-indigo-300' },
  { key: 'revisor',  label: 'Revisor',  cor: 'text-amber-300' },
  { key: 'consulta', label: 'Consulta', cor: 'text-slate-400' },
];

function SecaoPermissoes() {
  const { recarregar } = usePermissions();
  const [matriz, setMatriz] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/permissions').then(({ data }) => {
      const m = {};
      data.forEach(p => {
        if (!m[p.role]) m[p.role] = {};
        m[p.role][p.modulo] = { canView: p.canView, canEdit: p.canEdit, canSign: p.canSign };
      });
      // fill missing
      ROLES_CONFIG.forEach(r => {
        if (!m[r.key]) m[r.key] = {};
        MODULOS.forEach(mod => {
          if (!m[r.key][mod]) m[r.key][mod] = { canView: false, canEdit: false, canSign: false };
        });
      });
      setMatriz(m);
    }).catch(() => setErro('Erro ao carregar permissões.'));
  }, []);

  function toggle(role, modulo, campo) {
    if (role === 'consulta' && campo !== 'canView') return;
    setMatriz(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [modulo]: { ...prev[role][modulo], [campo]: !prev[role][modulo][campo] },
      },
    }));
  }

  async function salvar() {
    setSalvando(true); setErro(''); setSucesso('');
    const permissoes = [];
    ROLES_CONFIG.forEach(r => {
      MODULOS.forEach(mod => {
        permissoes.push({ role: r.key, modulo: mod, ...matriz[r.key][mod] });
      });
    });
    try {
      await api.put('/permissions', { permissoes });
      await recarregar();
      setSucesso('Permissões salvas com sucesso.');
      setTimeout(() => setSucesso(''), 3000);
    } catch {
      setErro('Erro ao salvar permissões.');
    } finally {
      setSalvando(false);
    }
  }

  if (!matriz) return <div className="h-32 animate-pulse bg-[var(--surface-1)] rounded-xl" />;

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-100 mb-1">Permissões por Perfil</h2>
          <p className="text-xs text-slate-500">
            Define o que cada perfil pode ver, editar ou assinar. Supervisor sempre tem acesso total.
            Consulta nunca pode editar ou assinar.
          </p>
        </div>
        <button
          onClick={salvar}
          disabled={salvando}
          className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          {salvando ? 'Salvando...' : '💾 Salvar'}
        </button>
      </div>

      {erro && <p className="text-red-400 text-xs mb-3">{erro}</p>}
      {sucesso && <p className="text-emerald-400 text-xs mb-3">✓ {sucesso}</p>}

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="text-left text-slate-500 font-medium py-2 pr-4 w-28">Módulo</th>
              {ROLES_CONFIG.map(r => (
                <th key={r.key} colSpan={3} className={`text-center font-semibold pb-2 ${r.cor}`}>
                  {r.label}
                </th>
              ))}
            </tr>
            <tr>
              <th />
              {ROLES_CONFIG.map(r => (
                ['Ver', 'Editar', 'Assinar'].map(a => (
                  <th key={`${r.key}-${a}`} className="text-center text-slate-600 font-normal pb-2 px-1 w-14">{a}</th>
                ))
              ))}
            </tr>
          </thead>
          <tbody>
            {MODULOS.map((mod, i) => (
              <tr key={mod} className={i % 2 === 0 ? 'bg-[var(--surface-1)]' : ''}>
                <td className="py-2 pr-4 text-slate-300 font-medium rounded-l-lg pl-2">
                  {MODULOS_LABEL[mod]}
                </td>
                {ROLES_CONFIG.map(r => {
                  const p = matriz[r.key][mod];
                  const isConsulta = r.key === 'consulta';
                  return (
                    <>
                      {/* canView */}
                      <td key={`${r.key}-${mod}-view`} className="text-center py-2 px-1">
                        <input
                          type="checkbox"
                          checked={!!p.canView}
                          onChange={() => toggle(r.key, mod, 'canView')}
                          className="w-3.5 h-3.5 accent-blue-500 cursor-pointer"
                        />
                      </td>
                      {/* canEdit */}
                      <td key={`${r.key}-${mod}-edit`} className="text-center py-2 px-1">
                        <input
                          type="checkbox"
                          checked={!!p.canEdit}
                          disabled={isConsulta}
                          onChange={() => toggle(r.key, mod, 'canEdit')}
                          className="w-3.5 h-3.5 accent-blue-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        />
                      </td>
                      {/* canSign */}
                      <td key={`${r.key}-${mod}-sign`} className="text-center py-2 px-1 rounded-r-lg">
                        <input
                          type="checkbox"
                          checked={!!p.canSign}
                          disabled={isConsulta || mod !== 'calibracoes'}
                          onChange={() => toggle(r.key, mod, 'canSign')}
                          className="w-3.5 h-3.5 accent-blue-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        />
                      </td>
                    </>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-slate-600 mt-3">
        * "Assinar" aplica-se apenas ao módulo Calibração (aprovação de laudos).
      </p>
    </div>
  );
}

// ─── Componente auxiliar: toggle on/off ──────────────────────────────────────
function ToggleOpcao({ ativo, onChange, rotulo, desc }) {
  return (
    <div className="flex items-start justify-between p-4 bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg">
      <div className="flex-1 mr-4">
        <p className="text-sm font-medium text-slate-200">{rotulo}</p>
        {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!ativo)}
        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
          ativo ? 'bg-blue-600' : 'bg-[var(--surface-4)]'
        }`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
          ativo ? 'translate-x-5' : 'translate-x-0'
        }`} />
      </button>
    </div>
  );
}